/**
 * JSON API Envelope Standard
 *
 * All API responses MUST be wrapped in this envelope structure.
 * Provides consistent metadata, error handling, and request tracing.
 */

/**
 * Error structure for failed API responses
 */
export interface EnvelopeError {
  /** Error code (e.g., VALIDATION_ERROR, NOT_FOUND, UNAUTHORIZED) */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Field name if error is field-specific (e.g., validation error) */
  field?: string;
  /** Additional error details or context */
  details?: Record<string, unknown>;
}

/**
 * Metadata attached to API responses
 */
export interface EnvelopeMetadata {
  /** API version */
  version?: string;
  /** Pagination info for list responses */
  pagination?: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
  /** Rate limit info */
  rateLimit?: {
    limit: number;
    remaining: number;
    reset: number; // Unix timestamp
  };
  /** Additional custom metadata */
  [key: string]: unknown;
}

/**
 * Generic API Response Envelope
 *
 * Wraps all API responses with consistent structure:
 * - requestId: Unique identifier for request tracing
 * - timestamp: ISO 8601 timestamp of response
 * - success: Boolean indicating success/failure
 * - data: Response payload (only on success)
 * - error: Error details (only on failure)
 * - metadata: Optional metadata (pagination, versioning, etc.)
 */
export interface Envelope<T = unknown> {
  /** Unique request identifier for tracing (format: req-{ulid}) */
  requestId: string;
  /** ISO 8601 timestamp of response */
  timestamp: string;
  /** Success flag */
  success: boolean;
  /** Response data (present only when success=true) */
  data?: T;
  /** Error details (present only when success=false) */
  error?: EnvelopeError;
  /** Optional metadata (pagination, version, etc.) */
  metadata?: EnvelopeMetadata;
}

/**
 * Success envelope type (data is guaranteed to be present)
 */
export type SuccessEnvelope<T> = Envelope<T> & {
  success: true;
  data: T;
  error?: never;
};

/**
 * Error envelope type (error is guaranteed to be present)
 */
export type ErrorEnvelope = Envelope<never> & {
  success: false;
  data?: never;
  error: EnvelopeError;
};

/**
 * Utility: Create a success envelope
 *
 * @param data - Response data payload
 * @param requestId - Optional request ID (auto-generated if not provided)
 * @param metadata - Optional metadata
 * @returns Success envelope wrapping the data
 *
 * @example
 * const response = createSuccessEnvelope({ userId: 'usr-123' }, 'req-abc');
 */
export function createSuccessEnvelope<T>(
  data: T,
  requestId?: string,
  metadata?: EnvelopeMetadata
): SuccessEnvelope<T> {
  return {
    requestId: requestId ?? `req-${Date.now()}`,
    timestamp: new Date().toISOString(),
    success: true,
    data,
    ...(metadata && { metadata }),
  };
}

/**
 * Utility: Create an envelope (defaults to success)
 *
 * Convenience alias for createSuccessEnvelope.
 * Use this for normal success responses.
 *
 * @param data - Response data payload
 * @param requestId - Optional request ID (auto-generated if not provided)
 * @param metadata - Optional metadata
 * @returns Success envelope wrapping the data
 *
 * @example
 * const response = createEnvelope({ userId: 'usr-123' });
 */
export function createEnvelope<T>(
  data: T,
  requestId?: string,
  metadata?: EnvelopeMetadata
): SuccessEnvelope<T> {
  return createSuccessEnvelope(data, requestId, metadata);
}

/**
 * Utility: Create an error envelope
 *
 * @param code - Error code
 * @param message - Error message
 * @param requestId - Optional request ID (auto-generated if not provided)
 * @param field - Optional field name for validation errors
 * @param details - Optional error details
 * @returns Error envelope
 *
 * @example
 * const response = createErrorEnvelope('NOT_FOUND', 'User not found', 'req-abc');
 */
export function createErrorEnvelope(
  code: string,
  message: string,
  requestId?: string,
  field?: string,
  details?: Record<string, unknown>
): ErrorEnvelope {
  return {
    requestId: requestId ?? `req-${Date.now()}`,
    timestamp: new Date().toISOString(),
    success: false,
    error: {
      code,
      message,
      ...(field && { field }),
      ...(details && { details }),
    },
  };
}

/**
 * Type guard: Check if envelope is successful
 *
 * @param envelope - API envelope
 * @returns True if envelope contains data (success case)
 */
export function isSuccessEnvelope<T>(
  envelope: Envelope<T>
): envelope is SuccessEnvelope<T> {
  return envelope.success === true && envelope.data !== undefined;
}

/**
 * Type guard: Check if envelope is an error
 *
 * @param envelope - API envelope
 * @returns True if envelope contains error (failure case)
 */
export function isErrorEnvelope(
  envelope: Envelope<unknown>
): envelope is ErrorEnvelope {
  return envelope.success === false && envelope.error !== undefined;
}
